import React from 'react'
import PropTypes from 'prop-types'
import NavigationHelper from 'layers/navigation/navigationHelper'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { SITE_QUERY_PARAM } from 'layers/content/v2/Hocs/withSiteContext/constants'
import { connect } from 'react-redux'
import { operations as loadingOperations } from 'store/loadingManager'

import {
  DASHBOARD_ROUTE,
  isHighSchoolUrl,
  isHighSchoolDashboard,
} from 'utils/highschoolHelpers'
import {
  isDigitalWellBeingUrl,
  isK5Url,
  isK5Dashboard,
  isMiddleSchoolUrl,
  isMiddleSchoolDashboard,
} from 'utils/routing/routeHelpers'

import SelectSiteModal from './component'

const SelectSiteModalContainer = props => {
  const {
    activeSites,
    currentSite,
    customRoute,
    handleClose,
    navigationEvent,
    setActiveSite,
    setDigitalWellBeingSiteContext,
    setElementarySiteContext,
    setMiddleSchoolSiteContext,
    setHighSchoolSiteContext,
    setAppIsLoadingState,
    show,
  } = props

  // Determine the appropriate dashboard route based on program type
  const getProgramDashboardRoute = () => {
    if (customRoute) return customRoute

    if (isHighSchoolUrl() || isHighSchoolDashboard()) {
      return '/dashboard/grades-9-12'
    } else if (isMiddleSchoolUrl() || isMiddleSchoolDashboard()) {
      return '/dashboard/grades-6-8'
    } else if (isK5Url() || isK5Dashboard()) {
      return '/dashboard/grades-k-5'
    } else {
      return DASHBOARD_ROUTE
    }
  }

  const redirectTo = getProgramDashboardRoute()

  // Determine the current program type to use the correct setter function
  const getCurrentProgramType = () => {
    if (isHighSchoolUrl() || isHighSchoolDashboard()) {
      return 'highSchool'
    } else if (isMiddleSchoolUrl() || isMiddleSchoolDashboard()) {
      return 'middleSchool'
    } else if (isK5Url() || isK5Dashboard()) {
      return 'k5'
    } else if (isDigitalWellBeingUrl()) {
      return 'digitalWellBeing'
    } else {
      return 'highSchool' // Default to highSchool if no specific program type is detected
    }
  }

  const handleSiteChange = async site => {
    const { id } = site

    setAppIsLoadingState('Loading new site...')

    // Use the appropriate setter based on the current program type
    const programType = getCurrentProgramType()

    // Wait for site context to be updated
    switch (programType) {
      case 'k5':
        await (setElementarySiteContext || setActiveSite?.k5)(id)
        break
      case 'middleSchool':
        await (setMiddleSchoolSiteContext || setActiveSite?.middleSchool)(id)
        break
      case 'digitalWellBeing':
        await (
          setDigitalWellBeingSiteContext || setActiveSite?.digitalWellBeing
        )(id)
        break
      default:
        // highSchool
        await (setHighSchoolSiteContext || setActiveSite?.highSchool)(id)
        break
    }

    // Determine if we should preserve the current page or redirect to dashboard
    const isOnDashboard =
      isHighSchoolDashboard() || isMiddleSchoolDashboard() || isK5Dashboard()

    let newUrl
    if (!id) {
      // If no site ID, always go to dashboard
      newUrl = DASHBOARD_ROUTE
    } else if (isOnDashboard) {
      // If already on dashboard, navigate to the appropriate dashboard with site
      newUrl = `${redirectTo}?${SITE_QUERY_PARAM}=${id}`
    } else {
      // Preserve current page, just update the site parameter
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set(SITE_QUERY_PARAM, id)
      newUrl = `${currentUrl.pathname}${currentUrl.search}`
    }

    navigationEvent(newUrl, NavigationHelper.types.OPEN)

    // Close the modal
    handleClose()
  }

  // If activeSites is not available, don't render the modal
  if (!activeSites) {
    return null
  }

  return (
    <SelectSiteModal
      activeSites={activeSites?.map(s => ({ name: s?.name, id: s?.id }))}
      currentSite={currentSite}
      handleClose={handleClose}
      handleSiteChange={handleSiteChange}
      show={show}
    />
  )
}

SelectSiteModalContainer.propTypes = {
  activeSites: PropTypes.array.isRequired,
  currentSite: PropTypes.object,
  customRoute: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  navigationEvent: PropTypes.func.isRequired,
  setActiveSite: PropTypes.object,
  setAppIsLoadingState: PropTypes.func.isRequired,
  setDigitalWellBeingSiteContext: PropTypes.func,
  setElementarySiteContext: PropTypes.func,
  setHighSchoolSiteContext: PropTypes.func,
  setMiddleSchoolSiteContext: PropTypes.func,
  show: PropTypes.bool.isRequired,
}

const mapDispatchToProps = {
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
}

export default connect(
  null,
  mapDispatchToProps,
)(withSiteContext(SelectSiteModalContainer))
